import { FC } from "react";
import Image from "apps/website/components/base/Image/Image";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import Text from "apps/website/components/base/Text/Text";
import {
  Interpolator,
  interpolateString,
} from "libs/form-utils/src/lib/interpolate";
import { getFlowFieldValuesForInterpolation } from "@./state";
import {
  legacySizeCollectionMap,
} from "apps/website/components/base/Text/Text.map";
import Column from "apps/website/components/layout/Column/Column";
import TaggedSummary from "apps/website/components/feature/TaggedSummary/TaggedSummary";
import {
  ITaggedSummaryGroupProps,
} from "apps/website/components/feature/TaggedSummary/TaggedSummaryGroup";
import { Recipe } from "@forms/schema";
import {
  RecipeLineup,
} from "apps/website/components/feature/RecipeLineup/RecipeLineup";
import {
  getFreshRecipeDisplayName,
} from "../../../form/UpdateRecipes/UpdateRecipes.map";

interface IFormServiceCatSummaryProps {
  flowSlug: string;
  linkingId: string;
  values: Record<string, string>;
}

export const FormServiceCatSummaryHighlightingRecipesTest: FC<IFormServiceCatSummaryProps> = (
  { values, flowSlug, linkingId },
) => {

  const groups: ITaggedSummaryGroupProps[] = [];
  const allergies = values.$catAllergies?.split(",") || [];
  const defaultRecipes: Recipe[] = values.$defaultRecipes === "undefined" ? [] : values.$defaultRecipes?.split(",") as Recipe[] || [];
  const defaultRecipeDisplayNames = defaultRecipes.map((recipe) => getFreshRecipeDisplayName(recipe));
  const calorieTags = values.$catKcalPerDay ? [ values.$catKcalPerDay ] : [];

  const aboutCatKeys = [ "$catAge", "$catSex", "$catPedigreeOrMoggie", "$catBreed", "$catActivity", "$catBodyCondition", "$catWeight" ];
  const aboutCatTags: string[] = [];
  aboutCatKeys.forEach((key) => {
    if (values[key] && values[key].trim() !== "") {
      const value = key === "$catWeight" ? `${values[key]}kg` : values[key];
      aboutCatTags.push(value);
    }
  });

  if (aboutCatTags.length) {
    groups.push({
      title: "Based on:",
      tags: aboutCatTags,
    });
  }

  if (calorieTags.length) {
    groups.push({
      title: `${values.$catPronounHeSheThey} needs to eat:`,
      tags: calorieTags,
    });
  }

  if (allergies.length) {
    groups.push({
      title: `We made sure ${values.$catPronounHisHerTheir} plan doesn't contain any:`,
      tags: allergies,
    });
  }

  groups.push({
    title: `Start with these ${defaultRecipes.length} recipes for ${values.$catName}'s smoothest switch to fresh:`,
    tags: defaultRecipeDisplayNames,
  });

  return (
    <>
      { !!groups.length && (
        <Column className="bg-brand" component="CatSummaryB" data-linkingid={linkingId}>
          <>
            <div className="flex items-center px-4 pt-4 space-x-4">
              <div className={"items-center justify-center relative aspect-[3/4] w-16 flex-none"}>
                <Image image={{
                  src: "https://a.storyblok.com/f/176726/290x381/f3b5cf231b/dom.png/m/320x0",
                  width: 290,
                  height: 381,
                }} alt="In house vet Dom" className={themeRootClassMap.green} />
                <Image image={{
                  src: "/images/features/team/frame.png",
                  height: 232,
                  width: 183,
                }} alt=""
                className="absolute top-0 left-0"
                />
              </div>
              <div className="flex items-center justify-center mr-4">
                <Text tag="h2" display="subtitle" size={legacySizeCollectionMap.titleSm} className="pb-0">Dom, our in-house cat nutrition expert, recommends the following for { interpolateString("[[$catName]]", getFlowFieldValuesForInterpolation(flowSlug, linkingId, "GET_RELATED"), Interpolator.FRONTEND) }:</Text>
              </div>
            </div>
            <TaggedSummary
              groups={groups}
            />
          </>
          <div className="p-4">
            <RecipeLineup recipes={defaultRecipes} />
          </div>
          <div className="px-4">
            <Text display="subtitle" align="center" size={legacySizeCollectionMap.titleSm}>Every recipe is complete + balanced. And perfectly portioned to meet { interpolateString("[[$catName]]", getFlowFieldValuesForInterpolation(flowSlug, linkingId, "GET_RELATED"), Interpolator.FRONTEND) }&apos;s daily calorie requirements.</Text>
          </div>
        </Column>
      ) }
    </>
  );
};
